import { FloatingInput } from '@/components/ui/floating-input';
import {
  StringValidationState,
  TransactionBuilderParameterInputProps,
  TransactionBuilderParameterState,
} from './types';
import { cn } from '@/lib/utils';
import { getParameterStateForString, getStringValidationState } from './utils';
import { getReadableSolidityName } from '@/lib/solidity-formatting';

export function StringInput({
  value,
  name,
  onChange,
  paramAbiFragment,
}: TransactionBuilderParameterInputProps): React.ReactElement {
  const label = getReadableSolidityName(
    paramAbiFragment.name || name,
    'Parameter'
  );
  const validationState = getStringValidationState(value);

  /**
   * Helper texts to display to the user when they're editing the string.
   */
  const helperTexts: Record<StringValidationState, string | undefined> = {
    [StringValidationState.VALID]: undefined,
    [StringValidationState.VALID_BUT_EMPTY]: 'Empty string',
    [StringValidationState.INVALID]: 'Invalid string',
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update form state
    const newState: TransactionBuilderParameterState =
      getParameterStateForString(event.target.value);
    // Call `onChange` callback
    onChange?.(name, newState);
  };

  const isError = validationState === StringValidationState.INVALID;
  const isWarn = validationState === StringValidationState.VALID_BUT_EMPTY;

  return (
    <div className="flex flex-col gap-1 w-full">
      <FloatingInput
        value={value}
        onChange={handleChange}
        label={label}
        placeholder="Some text"
        className={isError ? 'border-red-500 focus-visible:ring-red-500' : ''}
      />
      {helperTexts[validationState] && (
        <p
          className={cn(
            'text-xs px-1',
            isError
              ? 'text-red-500'
              : isWarn
                ? 'text-amber-500'
                : 'text-muted-foreground'
          )}
        >
          {helperTexts[validationState]}
        </p>
      )}
    </div>
  );
}
